import { IErrorResponse } from '@common/api.models';
import { AxiosError } from 'axios';
import { IHandledError } from './error.models';
import { lowerFirstLetter } from './Primitives/text.services';
import { triggerMsalLogout } from './auth/msalLogout.service';

const getIErrorResponse = async (error: AxiosError<IErrorResponse, any>): Promise<IErrorResponse | undefined> => {
  if (error.request.responseType === 'blob' && error?.response?.data instanceof Blob) {
    return JSON.parse(await error.response.data.text()) as IErrorResponse;
  }
  return error?.response?.data;
};

export const apiErrorHandle: (error: AxiosError<IErrorResponse>, genericErrorMessage: string) => IHandledError = (
  error,
  genericErrorMessage,
) => {
  const errorCode = error.response?.data?.code;
  if (!error.response?.status) {
    // system error
    return {
      errorType: 'DirectDisplayError',
      errorMessage: undefined,
      formErrors: undefined,
      errorCode,
    };
  }
  if (error.response?.status === 500) {
    return { errorType: 'DirectDisplayError', errorMessage: undefined, formErrors: undefined, errorCode };
  }
  if (error.response.status === 422) {
    const errorMessage = error.response.data.messages?.join('\n') || '';
    return {
      errorType: 'DirectDisplayError',
      errorMessage: errorMessage.length <= 0 ? undefined : errorMessage,
      formErrors: undefined,
      errorCode,
    };
  }
  if (error.response.status === 403) {
    // TODO: not enough permission handle
    return {
      errorType: 'DirectDisplayError',
      errorMessage: 'You do not have enough permissions to perform this action',
      formErrors: undefined,
      errorCode,
    };
  }
  if (error.response.status === 401) {
    const url = error.config?.url || '';

    // Check if this is the specific case: Me API + no token = token expired
    if (url.includes('/Users/Me')) {
      console.error('Token expired - Me API called without token:', {
        url: error.config?.url,
        timestamp: new Date().toISOString(),
      });

      // Prevent infinite redirect loops
      const hasAttemptedLogout = sessionStorage.getItem('gems-auth-retry') === 'attempted';

      if (!hasAttemptedLogout) {
        sessionStorage.setItem('gems-auth-retry', 'attempted');

        setTimeout(() => {
          sessionStorage.removeItem('gems-auth-retry');

          triggerMsalLogout();
        }, 2000);

        return {
          errorType: 'DirectDisplayError',
          errorMessage: 'Your session has expired. Logging out...',
          formErrors: undefined,
          errorCode,
        };
      }

      // Already tried logout/redirect
      sessionStorage.removeItem('gems-auth-retry');
      return {
        errorType: 'DirectDisplayError',
        errorMessage: 'Authentication failed. Please try logging out and logging in again.',
        formErrors: undefined,
        errorCode,
      };
    }

    // All other 401s - show the error message
    return {
      errorType: 'DirectDisplayError',
      errorMessage: 'You do not have enough permission to perform this action',
      formErrors: undefined,
      errorCode,
    };
  }
  if (error.response.status === 400) {
    const errors = error?.response?.data?.errors ?? {};
    const returnError: { [key: string]: string } = {};
    Object.keys(errors).forEach((errorKey) => {
      const split = errorKey.split('.');
      const key = split.map((splitItem) => lowerFirstLetter(splitItem)).join('.');
      const item = errors[errorKey];
      if (!item) {
        return;
      }
      if (typeof item === 'string') {
        returnError[key] = item;
      } else returnError[key] = (item as string[]).join('\n');
    });

    return {
      errorType: 'FormError',
      errorMessage: undefined,
      formErrors: returnError,
      errorCode,
    };
  }
  // Directly display error for debugging purpose
  if (process.env.NODE_ENV === 'development') {
    return {
      errorType: 'DirectDisplayError',
      errorMessage: error.message,
      formErrors: undefined,
      errorCode,
    };
  }

  return {
    errorType: 'DirectDisplayError',
    errorMessage: 'Unexpected error occurred, please contact support to help.',
    formErrors: undefined,
    errorCode,
  };
};
